import { differenceInCalendarDays, parseISO } from 'date-fns'
import type {
  InsightMeta,
  PeriodType,
  TimeSeriesComparisonPayload,
  SeriesNormalized,
} from '../visualization.types'

export type ComparisonKind = 'YoY' | 'MoM' | 'YTD'

export interface ComputedChange {
  kind: ComparisonKind
  referenceTotal: number
  comparisonTotal: number
  percentChange: number // em +/-% , ex.: -16.7
  alignedDays?: number // dias utilizados na comparação (quando parcial)
}

// Soma com alinhamento por dias (usa o menor comprimento)
export function sumAligned(a: number[], b: number[]) {
  const n = Math.min(a.length, b.length)
  const sliceA = a.slice(0, n).reduce((acc, v) => acc + (v ?? 0), 0)
  const sliceB = b.slice(0, n).reduce((acc, v) => acc + (v ?? 0), 0)
  return { sliceA, sliceB, n }
}

// Soma simples de todos os valores
export function sumAll(xs: number[]) {
  return xs.reduce((acc, value) => acc + (value ?? 0), 0)
}

// Quantos dias possui a janela (start..end) no meta
function windowDays(meta?: InsightMeta, which: 'reference' | 'comparison') {
  const start = meta?.[which]?.start
  const end = meta?.[which]?.end
  if (!start || !end) return undefined
  return differenceInCalendarDays(parseISO(end), parseISO(start)) + 1
}

export function inferKind(periodType: PeriodType | undefined, meta?: InsightMeta): ComparisonKind {
  if (periodType === 'year') return 'YTD'
  if (periodType === 'month') {
    // Se os anos de ref/comparison forem diferentes -> YoY; se não -> MoM
    const referenceYear = meta?.reference?.start
      ? parseISO(meta.reference.start).getUTCFullYear()
      : undefined
    const comparisonYear = meta?.comparison?.start
      ? parseISO(meta.comparison.start).getUTCFullYear()
      : undefined
    return referenceYear && comparisonYear && referenceYear !== comparisonYear ? 'YoY' : 'MoM'
  }
  // Fallback
  return 'YoY'
}

export function computeChange(
  payload: TimeSeriesComparisonPayload,
  series: SeriesNormalized[],
): ComputedChange | undefined {
  if (!Array.isArray(series) || series.length < 2) return

  const kind = inferKind(payload.meta?.periodType, payload.meta)

  const reference = series[1]?.values ?? []
  const comparison = series[0]?.values ?? []

  if (typeof payload?.meta?.percentageChange === 'number') {
    return {
      kind,
      referenceTotal: sumAll(reference),
      comparisonTotal: sumAll(comparison),
      percentChange: payload.meta.percentageChange * 100,
    }
  }

  // Alinhamentos
  const refWindow = windowDays(payload.meta, 'reference')

  if (kind === 'YTD') {
    // Ano corrente até o DOY atual vs mesmo DOY do ano anterior
    const days = Math.min(refWindow ?? reference.length, reference.length) // garante não extrapolar a série
    const { sliceA, sliceB, n } = sumAligned(reference.slice(0, days), comparison)
    const percentChange = sliceB === 0 ? 0 : ((sliceA - sliceB) / sliceB) * 100
    return { kind, referenceTotal: sliceA, comparisonTotal: sliceB, percentChange, alignedDays: n }
  }

  if (kind === 'YoY') {
    // Mês atual vs mesmo mês ano anterior — se parcial, usa os primeiros D dias de ambos
    if (refWindow && refWindow > 0) {
      const days = Math.min(refWindow, reference.length)
      const { sliceA, sliceB, n } = sumAligned(reference.slice(0, days), comparison)
      const percentChange = sliceB === 0 ? 0 : ((sliceA - sliceB) / sliceB) * 100
      return {
        kind,
        referenceTotal: sliceA,
        comparisonTotal: sliceB,
        percentChange,
        alignedDays: n,
      }
    }
    const sliceA = sumAll(reference)
    const sliceB = sumAll(comparison)
    const percentChange = sliceB === 0 ? 0 : ((sliceA - sliceB) / sliceB) * 100
    return { kind, referenceTotal: sliceA, comparisonTotal: sliceB, percentChange }
  }

  // MoM
  {
    // Último mês parcial (ou completo) vs mês anterior
    if (refWindow && refWindow > 0) {
      const days = Math.min(refWindow, reference.length)
      const { sliceA, sliceB, n } = sumAligned(reference.slice(0, days), comparison)
      const percentChange = sliceB === 0 ? 0 : ((sliceA - sliceB) / sliceB) * 100
      return {
        kind,
        referenceTotal: sliceA,
        comparisonTotal: sliceB,
        percentChange,
        alignedDays: n,
      }
    }
    const sliceA = sumAll(reference)
    const sliceB = sumAll(comparison)
    const percentChange = sliceB === 0 ? 0 : ((sliceA - sliceB) / sliceB) * 100
    return { kind, referenceTotal: sliceA, comparisonTotal: sliceB, percentChange }
  }
}
